import React, { useState, useEffect } from "react";
import { FiSave, FiX, FiFileText } from "react-icons/fi";
import api from '../api/api';

const MeetingReportModal = ({ isOpen, onClose, meeting }) => {
  const [reportId, setReportId] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    if (!isOpen || !meeting?.id) return;

    const fetchReport = async () => {
      setLoading(true);
      setReportId(null);
      setContent("");
      setUpdatedAt(null);
      try {
        const res = await api.get('/meeting-reports/');
        const existing = res.data.find((r) => r.meeting === meeting.id);
        if (existing) {
          setReportId(existing.id);
          setContent(existing.content || "");
          setUpdatedAt(existing.updated_at);
        }
      } catch (error) {
        console.error('خطا در دریافت گزارش جلسه:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [isOpen, meeting?.id]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!meeting?.id) return;
    setSaving(true);
    try {
      if (reportId) {
        const res = await api.patch(`/meeting-reports/${reportId}/`, { content });
        setUpdatedAt(res.data.updated_at);
      } else {
        const res = await api.post('/meeting-reports/', { meeting: meeting.id, content });
        setReportId(res.data.id);
        setUpdatedAt(res.data.updated_at);
      }
      window.alert('گزارش جلسه ذخیره شد.');
    } catch (error) {
      console.error('خطا در ذخیره گزارش جلسه:', error);
      const backendMsg = error.response?.data ? JSON.stringify(error.response.data) : 'خطای نامشخص';
      window.alert('خطا در ذخیره گزارش: ' + backendMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs" onClick={onClose} />
      <div className="bg-white w-full max-w-lg rounded-3xl p-6 shadow-2xl relative z-10" dir="rtl">
        <button onClick={onClose} className="absolute top-4 left-4 text-slate-400 hover:text-slate-600"><FiX /></button>

        <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
          <FiFileText className="text-indigo-600" /> گزارش جلسه
        </h2>
        <p className="text-sm text-slate-600 mb-6 font-bold">{meeting?.title}</p>

        {/* جزئیات جلسه */}
        <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 col-span-2">
            <span className="text-xs text-slate-400 block">زمان برگزاری:</span>
            <span className="font-bold text-slate-700">
              {meeting?.scheduled_at ? new Date(meeting.scheduled_at).toLocaleString('fa-IR') : "---"}
            </span>
          </div>
          <div className="col-span-2 bg-slate-50 p-3 rounded-xl border border-slate-100">
            <span className="text-xs text-slate-400 block">لینک/آدرس جلسه:</span>
            <span className="text-indigo-600 font-bold truncate block">{meeting?.locationorlink || "---"}</span>
          </div>
        </div>

        {meeting?.description && (
          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 mb-6">
            <p className="text-xs text-slate-500 mb-1">توضیحات جلسه:</p>
            <p className="text-sm text-slate-700">{meeting.description}</p>
          </div>
        )}

        <div className="mb-2">
          <label className="text-sm font-bold text-slate-800 block mb-2">متن صورت‌جلسه / مصوبات:</label>
          {loading ? (
            <div className="text-center py-4 text-sm text-slate-500">در حال بارگذاری گزارش...</div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows="8"
              placeholder="خلاصه مذاکرات، تصمیمات و مصوبات جلسه را اینجا بنویسید..."
              className="w-full px-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:border-indigo-500 resize-none"
            />
          )}
        </div>

        {updatedAt && (
          <p className="text-[10px] text-slate-400 mb-4">
            آخرین به‌روزرسانی: {new Date(updatedAt).toLocaleString('fa-IR')}
          </p>
        )}

        <button
          onClick={handleSave}
          disabled={saving || loading}
          className="w-full py-3 bg-indigo-600 text-white font-bold text-sm rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <FiSave /> {saving ? "در حال ذخیره..." : "ذخیره گزارش"}
        </button>
      </div>
    </div>
  );
};

export default MeetingReportModal;
